/**
 * Per-page SEO component for React 19.
 *
 * Strategy: React 19's native head hoisting is unreliable when the static
 * public/index.html also ships <title>/<link rel=canonical>. To guarantee
 * crawlers + share previews see the right values, we ALSO imperatively
 * mutate document.title + canonical/meta tags via useEffect. The hoisted
 * JSX still benefits SSR/streaming if we ever migrate.
 *
 * Usage:
 *   <SEO title="Pricing" description="..." path="/pricing" jsonLd={...} />
 */
import { useEffect } from "react";

const SITE_URL = "https://lumoraai.com.au";
const DEFAULT_OG = `${SITE_URL}/og-image.png`;
const JSONLD_ID = "lumora-page-jsonld";

function setMeta(attr, name, content) {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(jsonLd) {
  // Remove the previous page-specific JSON-LD block so we don't accumulate.
  const prev = document.getElementById(JSONLD_ID);
  if (prev) prev.remove();
  if (!jsonLd) return;
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = JSONLD_ID;
  script.textContent = JSON.stringify(jsonLd);
  document.head.appendChild(script);
}

export function SEO({
  title,
  description,
  path = "/",
  image,
  noIndex = false,
  jsonLd,
}) {
  const fullTitle = title
    ? `${title} | Lumora AI`
    : "Lumora AI | AI-Powered UGC Ad Generator for E-Commerce & Fanvue";
  const desc =
    description ||
    "Generate hyper-realistic AI influencer models, product ads, and UGC video content for e-commerce brands and Fanvue creators.";
  const url = `${SITE_URL}${path}`;
  const og = image || DEFAULT_OG;

  useEffect(() => {
    document.title = fullTitle;
    setCanonical(url);
    setMeta("name", "description", desc);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:url", url);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:image", og);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", desc);
    setMeta("name", "twitter:image", og);
    if (noIndex) setMeta("name", "robots", "noindex, nofollow");
    setJsonLd(jsonLd);
  }, [fullTitle, desc, url, og, noIndex, jsonLd]);

  // No JSX needed — useEffect handles everything imperatively to guarantee
  // the values win over the static public/index.html defaults.
  return null;
}
